import {
  addDoc,
  collection,
  writeBatch,
  doc,
  getDocs,
  updateDoc,
  query,
  orderBy,
  getDoc,
  where,
  limit,
} from "@firebase/firestore";
import { db } from "../config/firebase-config";
import {
  ParticipantsList,
  RoomAttendanceCount,
  RoomAttendanceTime,
  RoomDetailsType,
} from "../types/common";

export const UseAddRoomParticipants = () => {
  const batch = writeBatch(db);
  const addRoomParticipants = async (data: RoomDetailsType[]) => {
    try {
      const event1CollectionRef = collection(db, "Symposium_Event_Room");

      data.forEach(async (item) => {
        batch.set(doc(event1CollectionRef), item);
      });

      await batch.commit();
      return { success: true };
    } catch (e) {
      console.error(e);
      return { success: false, data: e };
    }
  };

  return { addRoomParticipants };
};

export const getParticipantsAttendance = async () => {
  try {
    const event1CollectionRef = collection(db, "Symposium_Event_Room");
    const q = query(event1CollectionRef, orderBy("code", "asc"));
    const snapshot = await getDocs(q);
    const participants = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));
    return { success: true, data: participants };
  } catch (e) {
    console.error(e);
    return { success: false, data: e };
  }
};

export const updateParticipant = async (id: string) => {
  try {
    const event1CollectionRef = collection(db, "Symposium_Event_Room");
    const participantDocRef = doc(event1CollectionRef, id);

    const currentDate = new Date();
    const updatedData = {
      clockedIn: currentDate,
    };

    await updateDoc(participantDocRef, updatedData);
    const updatedDoc = await getDoc(participantDocRef);

    if (updatedDoc.exists()) {
      return { success: true, data: updatedDoc.data() };
    } else {
      return { success: false, data: "Document does not exist after update." };
    }
  } catch (e) {
    console.error(e);
    return { success: false, data: e };
  }
};

export const getParticipantById = async (id: string) => {
  try {
    const event1CollectionRef = collection(db, "Symposium_Event");
    const participantDocRef = doc(event1CollectionRef, id);
    const docSnap = await getDoc(participantDocRef);
    if (docSnap.exists()) {
      if (!docSnap.data().clockedIn) {
        return await updateParticipant(id)
          .then((res) => {
            return { success: true, data: res.data, isClockedIn: false };
          })
          .catch((e) => {
            return { success: false, data: e, isClockedIn: false };
          });
      } else {
        return { success: true, data: docSnap.data(), isClockedIn: true };
      }
    } else {
      return { success: false, data: "No such document!", isClockedIn: false };
    }
  } catch (e) {
    console.error(e);
    return { success: false, data: e, isClockedIn: false };
  }
};

export const getParticipantByIdForRoom = async (id: string) => {
  try {
    const event1CollectionRef = collection(db, "Symposium_Event");
    const participantDocRef = doc(event1CollectionRef, id);
    const docSnap = await getDoc(participantDocRef);
    if (docSnap.exists()) {
      return { success: true, data: docSnap.data() };
    } else {
      return { success: false, data: "No such document!" };
    }
  } catch (e) {
    console.error(e);
    return { success: false, data: e };
  }
};

export async function updateRoomDetails(
  roomDetails: RoomDetailsType,
  id: string,
  room: number,
  day: number,
  participant: ParticipantsList
): Promise<number> {
  const res = await getRoomDetailsWithMaxAttemptSeq(id, room, day);
  if (res) {
    roomDetails.attemptSeq = res.attemptSeq! + 1;
    await addRoomDetails(roomDetails);
    return roomDetails.attemptSeq;
  } else {
    roomDetails.attemptSeq = 1;
    roomDetails.dealershipName = participant?.dealershipName;
    roomDetails.district = participant?.district;
    roomDetails.name = participant?.name;
    roomDetails.email = participant?.email;
    roomDetails.phone = participant?.phone;

    await addRoomDetails(roomDetails);
    return roomDetails.attemptSeq;
  }
}

export async function addRoomDetails(
  roomDetails: RoomDetailsType
): Promise<string> {
  const roomDetailsRef = collection(db, "Symposium_Event_Room");
  const docRef = await addDoc(roomDetailsRef, roomDetails);
  return docRef.id;
}

export async function getRoomDetailsWithMaxAttemptSeq(
  id: string,
  room: number,
  day: number
): Promise<RoomDetailsType | null> {
  const roomDetailsRef = collection(db, "Symposium_Event_Room");
  const q = query(
    roomDetailsRef,
    where("id", "==", id),
    where("roomNo", "==", room),
    where("day", "==", day),
    orderBy("attemptSeq", "desc"),
    limit(1)
  );
  const querySnapshot = await getDocs(q);
  if (!querySnapshot.empty) {
    return querySnapshot.docs[0].data() as RoomDetailsType;
  }
  return null;
}

export async function getRoomAttendanceCountDetails(): Promise<RoomAttendanceCount> {
  let roomDetails: RoomAttendanceCount = {
    roomOneDayOne: await getRoomAttendanceCountByDateAndRoomNo(1, 1),
    roomTwoDayOne: await getRoomAttendanceCountByDateAndRoomNo(1, 2),
    roomThreeDayOne: await getRoomAttendanceCountByDateAndRoomNo(1, 3),
    roomOneDayTwo: await getRoomAttendanceCountByDateAndRoomNo(2, 1),
    roomTwoDayTwo: await getRoomAttendanceCountByDateAndRoomNo(2, 2),
    roomThreeDayTwo: await getRoomAttendanceCountByDateAndRoomNo(2, 3),
  };
  return roomDetails;
}

export async function getRoomAttendanceCountByDateAndRoomNo(
  day: number,
  room: number
): Promise<number> {
  const roomDetailsRef = collection(db, "Symposium_Event_Room");
  const q = query(
    roomDetailsRef,
    where("roomNo", "==", room),
    where("day", "==", day),
    where("attemptSeq", "==", 1)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.size; // Returns the count of documents matching the query
}

export async function getRoomAttendanceDetailsByDateAndRoomNo(
  day: number,
  room: number
): Promise<RoomDetailsType[]> {
  const roomDetailsRef = collection(db, "Symposium_Event_Room");
  const q = query(
    roomDetailsRef,
    where("roomNo", "==", room),
    where("day", "==", day),
    where("attemptSeq", "==", 1)
  );
  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map((doc) => doc.data() as RoomDetailsType);
}

export async function getRoomAttendanceByUserId(
  id: string
): Promise<RoomAttendanceTime> {
  const roomDetailsRef = collection(db, "Symposium_Event_Room");
  const q = query(
    roomDetailsRef,
    where("id", "==", id),
    where("attemptSeq", "==", 1)
  );
  let roomAttendance: RoomAttendanceTime = {
    roomOneDayOne: "",
    roomTwoDayOne: "",
    roomThreeDayOne: "",
    roomOneDayTwo: "",
    roomTwoDayTwo: "",
    roomThreeDayTwo: "",
  };
  const querySnapshot = await getDocs(q);
  querySnapshot.forEach((doc) => {
    if (doc.data().day === 1 && doc.data().roomNo === 1) {
      roomAttendance.roomOneDayOne = doc.data().clockedIn as string;
    } else if (doc.data().day === 1 && doc.data().roomNo === 2) {
      roomAttendance.roomTwoDayOne = doc.data().clockedIn as string;
    } else if (doc.data().day === 1 && doc.data().roomNo === 3) {
      roomAttendance.roomThreeDayOne = doc.data().clockedIn as string;
    } else if (doc.data().day === 2 && doc.data().roomNo === 1) {
      roomAttendance.roomOneDayTwo = doc.data().clockedIn as string;
    } else if (doc.data().day === 2 && doc.data().roomNo === 2) {
      roomAttendance.roomTwoDayTwo = doc.data().clockedIn as string;
    } else if (doc.data().day === 2 && doc.data().roomNo === 3) {
      roomAttendance.roomThreeDayTwo = doc.data().clockedIn as string;
    }
  });

  return roomAttendance;
}

// New optimized method to fetch all room attendance data at once
export async function getAllRoomAttendanceData(): Promise<
  Map<string, RoomAttendanceTime>
> {
  const roomDetailsRef = collection(db, "Symposium_Event_Room");
  const q = query(roomDetailsRef, where("attemptSeq", "==", 1));

  const roomAttendanceMap = new Map<string, RoomAttendanceTime>();

  try {
    const querySnapshot = await getDocs(q);

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      const userId = data.id as string;
      const day = data.day as number;
      const roomNo = data.roomNo as number;
      const clockedIn = data.clockedIn as string;

      // Initialize user's room attendance if not exists
      if (!roomAttendanceMap.has(userId)) {
        roomAttendanceMap.set(userId, {
          roomOneDayOne: "",
          roomTwoDayOne: "",
          roomThreeDayOne: "",
          roomOneDayTwo: "",
          roomTwoDayTwo: "",
          roomThreeDayTwo: "",
        });
      }

      const userAttendance = roomAttendanceMap.get(userId)!;

      // Map the room attendance based on day and room number
      if (day === 1 && roomNo === 1) {
        userAttendance.roomOneDayOne = clockedIn;
      } else if (day === 1 && roomNo === 2) {
        userAttendance.roomTwoDayOne = clockedIn;
      } else if (day === 1 && roomNo === 3) {
        userAttendance.roomThreeDayOne = clockedIn;
      } else if (day === 2 && roomNo === 1) {
        userAttendance.roomOneDayTwo = clockedIn;
      } else if (day === 2 && roomNo === 2) {
        userAttendance.roomTwoDayTwo = clockedIn;
      } else if (day === 2 && roomNo === 3) {
        userAttendance.roomThreeDayTwo = clockedIn;
      }
    });

    return roomAttendanceMap;
  } catch (error) {
    console.error("Error fetching all room attendance data:", error);
    return new Map();
  }
}

// Helper function to get room attendance for a specific user from the cached data
export function getRoomAttendanceFromCache(
  userId: string,
  roomAttendanceCache: Map<string, RoomAttendanceTime>
): RoomAttendanceTime {
  return (
    roomAttendanceCache.get(userId) || {
      roomOneDayOne: "",
      roomTwoDayOne: "",
      roomThreeDayOne: "",
      roomOneDayTwo: "",
      roomTwoDayTwo: "",
      roomThreeDayTwo: "",
    }
  );
}
